import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

const INK = '#1A2230'

interface BuckyMascotProps {
    size?: number
    className?: string
    title?: string
    decorative?: boolean
}

// Bucky brand mascot: simple rounded orange head, dark circular glasses, subtle smile.
// No body, limbs, teeth or animal features. Flat vector, drawn on a 120 x 120 grid.
export const BuckyMascot: FC<BuckyMascotProps> = ({
    size = 96,
    className,
    title = 'Bucky, the BUCKY AI mascot',
    decorative = false
}): ReactNode => {
    return (
        <svg
            width={size}
            height={size}
            viewBox='0 0 120 120'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className={cn('shrink-0', className)}
            role={decorative ? undefined : 'img'}
            aria-hidden={decorative ? true : undefined}
        >
            {!decorative && <title>{title}</title>}

            <circle cx='60' cy='60' r='54' fill='#FF8A3D' />

            <path
                d='M22 50 A40 40 0 0 1 50 21'
                stroke='#FFB37F'
                strokeWidth='5'
                strokeLinecap='round'
                opacity='0.75'
            />

            <path d='M28.5 54 L15 51' stroke={INK} strokeWidth='5' strokeLinecap='round' />
            <path d='M91.5 54 L105 51' stroke={INK} strokeWidth='5' strokeLinecap='round' />

            <circle cx='42' cy='56' r='14' fill={INK} fillOpacity='0.08' stroke={INK} strokeWidth='6' />
            <circle cx='78' cy='56' r='14' fill={INK} fillOpacity='0.08' stroke={INK} strokeWidth='6' />

            <path d='M56 54.5 Q60 50.5 64 54.5' stroke={INK} strokeWidth='5' strokeLinecap='round' />

            <circle cx='42' cy='57' r='4' fill={INK} />
            <circle cx='78' cy='57' r='4' fill={INK} />

            <path d='M35 50.5 A8 8 0 0 1 40.5 47' stroke='#FFFFFF' strokeWidth='2.5' strokeLinecap='round' opacity='0.75' />
            <path d='M71 50.5 A8 8 0 0 1 76.5 47' stroke='#FFFFFF' strokeWidth='2.5' strokeLinecap='round' opacity='0.75' />

            <path d='M49 82 Q60 89.5 71 82' stroke={INK} strokeWidth='4.5' strokeLinecap='round' />
        </svg>
    )
}

interface BuckyAvatarProps {
    size?: number
    speaking?: boolean
    className?: string
}

// The small circular companion Bucky uses on top of a game screen.
export const BuckyAvatar: FC<BuckyAvatarProps> = ({
    size = 48,
    speaking = false,
    className
}): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex items-center justify-center rounded-full bg-navy-900 p-[3px] ring-2 ring-orange/70',
                speaking && 'bucky-speaking',
                className
            )}
        >
            <BuckyMascot size={size} decorative />
        </span>
    )
}
